import { FormEvent, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { ApiError } from "../../../services/apiClient";
import { resetPassword } from "../api/auth";
import { PasswordField } from "../../../components/PasswordField";
import { FormFieldLabel } from "../../../components/FormFieldLabel";
import { AuthFormBase } from "../components/AuthFormBase";

const primaryButtonClasses = "pm-btn pm-btn-primary w-full";
const inputClasses =
  "block w-full rounded-xl border-0 py-2.5 text-slate-900 shadow-sm ring-1 ring-inset ring-slate-200 placeholder:text-slate-400 focus:ring-2 focus:ring-inset focus:ring-sky-600 sm:text-sm sm:leading-6 disabled:bg-slate-50 disabled:text-slate-500 disabled:ring-slate-200 dark:bg-slate-900 dark:text-slate-100 dark:ring-slate-700 dark:placeholder-slate-500 dark:disabled:bg-slate-800";
const fieldClasses = "flex flex-col gap-2";

export function ResetPasswordPage() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const initialToken = useMemo(() => params.get("token") ?? "", [params]);

  const [token, setToken] = useState(initialToken);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);
    setMessage(null);

    if (!token.trim()) {
      setError(t("auth.reset_password.token_required"));
      return;
    }
    if (password.trim().length < 8) {
      setError(t("auth.reset_password.password_too_short"));
      return;
    }
    if (password.trim() !== confirmPassword.trim()) {
      setError(t("auth.reset_password.passwords_mismatch"));
      return;
    }

    setIsSubmitting(true);
    try {
      const responseMessage = await resetPassword({
        token: token.trim(),
        password: password.trim(),
      });
      setMessage(responseMessage ?? t("auth.reset_password.success_message"));
      setPassword("");
      setConfirmPassword("");
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.body?.message ?? t("auth.reset_password.error"));
      } else {
        setError(t("common.error.unexpected"));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AuthFormBase
      onSubmit={handleSubmit}
      error={error}
      success={message}
      showSocialAuth={false}
      submitButton={
        <button type="submit" className={primaryButtonClasses} disabled={isSubmitting}>
          {isSubmitting ? t("auth.reset_password.updating") : t("auth.reset_password.button")}
        </button>
      }
    >
      <div className={fieldClasses}>
        <label htmlFor="reset-token">
          <FormFieldLabel label={t("auth.reset_password.token_label")} />
        </label>
        <input
          id="reset-token"
          type="text"
          className={inputClasses}
          value={token}
          onChange={(event) => setToken(event.target.value)}
          required
        />
        <small className="mt-1 text-sm text-slate-500 dark:text-slate-400">{t("auth.reset_password.token_helper")}</small>
      </div>

      <div className={fieldClasses}>
        <label htmlFor="reset-password">
          <FormFieldLabel label={t("forms.password.new_label")} />
        </label>
        <PasswordField
          id="reset-password"
          className={inputClasses}
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          autoComplete="new-password"
          required
          disabled={isSubmitting}
          showLabel={t("forms.password.show_new_label")}
          hideLabel={t("forms.password.hide_new_label")}
        />
      </div>

      <div className={fieldClasses}>
        <label htmlFor="reset-password-confirm">
          <FormFieldLabel label={t("forms.password.confirm_new_label")} />
        </label>
        <PasswordField
          id="reset-password-confirm"
          className={inputClasses}
          value={confirmPassword}
          onChange={(event) => setConfirmPassword(event.target.value)}
          autoComplete="new-password"
          required
          disabled={isSubmitting}
          showLabel={t("forms.password.show_confirm_label")}
          hideLabel={t("forms.password.hide_confirm_label")}
        />
      </div>
    </AuthFormBase>
  );
}
